import { inject, observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from '../t';
import http from '../http';
import { copyImageToClipboard } from '../util';
import C from '../colors';

function Row({ label, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 16 }}>
      <span style={{ fontFamily: C.mono, fontSize: 11, color: C.ink3, letterSpacing: 0.5, width: 52, paddingTop: 12, flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

function PillGroup({ value, onChange, options }) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {options.map(([v, l]) => (
        <button key={v} onClick={() => onChange(v)} style={{
          padding: '7px 16px', borderRadius: 20,
          border: `1.5px solid ${value === v ? C.brand : C.hair}`,
          background: value === v ? C.brandSoft : '#fff',
          color: value === v ? C.brand2 : C.ink3,
          fontFamily: C.sans, fontSize: 13, fontWeight: value === v ? 600 : 400,
          cursor: 'pointer', transition: 'all .15s',
        }}>{l}</button>
      ))}
    </div>
  );
}

const ShareDialog = inject('snackbarStore', 'ordersStore')(observer(({ snackbarStore, ordersStore }) => {
  const { t } = useTranslation();
  const [expire, setExpire] = useState('7');
  const [isShared, setIsShared] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordType, setPasswordType] = useState('random');
  const [passwordError, setPasswordError] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [autoFill, setAutoFill] = useState(false);
  const [url, setUrl] = useState('');

  useEffect(() => {
    if (!ordersStore.shareDialogData) return;
    const pwd = ordersStore.shareDialogData?.Album?.password || '';
    if (pwd) {
      setIsShared(true);
      setPassword(pwd);
      http.get(`/user/order/${ordersStore.shareDialogData?.id}/qrCode`).then(res => {
        setQrCodeUrl(res.data.imageData);
      });
    } else {
      setIsShared(false); setPassword(''); setQrCodeUrl('');
    }
  }, [ordersStore.shareDialogData]);

  useEffect(() => {
    const base = `${window.location.origin}/customer/order/${ordersStore.shareDialogData?.id}/select`;
    setUrl(autoFill ? `${base}?pwd=${password}` : base);
  }, [ordersStore.shareDialogData, autoFill, password]);

  const passwordRegex = /^[A-Za-z0-9]{4}$/;
  const generateCode = () => String(Math.floor(Math.random() * 10000)).padStart(4, '0');

  const validate = (pwd) => passwordRegex.test(pwd) ? '' : '提取码需为 4 位数字或字母';

  const save = () => {
    const pwd = passwordType === 'random' ? generateCode() : password;
    const err = validate(pwd);
    if (err) { setPasswordError(err); return; }
    http.post(`/user/orders/${ordersStore.shareDialogData?.id}/setShare`, { password: pwd, expire })
      .then(() => http.get(`/user/order/${ordersStore.shareDialogData?.id}/qrCode`))
      .then(res => {
        setIsShared(true); setPassword(pwd); setQrCodeUrl(res.data.imageData);
        snackbarStore.showSnackbar('分享已保存');
        ordersStore.fetchOrders();
      }).catch(() => {});
  };

  const cancel = () => {
    if (Number(ordersStore.shareDialogData?.selectionNum || 0) > 0) return;
    http.get(`/user/orders/${ordersStore.shareDialogData?.id}/cancelShare`).then(() => {
      setIsShared(false); setPassword(''); setPasswordType('random'); setExpire('7'); setQrCodeUrl('');
      snackbarStore.showSnackbar('已取消分享');
      ordersStore.fetchOrders();
    }).catch(() => {});
  };

  const copy = async () => {
    const text = autoFill ? url : `链接: ${url}  提取码: ${password}`;
    try { await navigator.clipboard.writeText(text); snackbarStore.showSnackbar('已复制到剪贴板'); }
    catch { snackbarStore.showSnackbar('复制失败', 'error'); }
  };

  if (!ordersStore.shareDialogData) return null;
  const hasSelections = Number(ordersStore.shareDialogData.selectionNum || 0) > 0;

  return (
    <div onClick={() => ordersStore.setShareDialogData(null)} style={{
      position: 'fixed', inset: 0, zIndex: 1200,
      background: 'rgba(26,20,23,.45)',
      backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: C.paper, borderRadius: 16,
        border: `1px solid ${C.hair}`,
        width: '100%', maxWidth: isShared && qrCodeUrl ? 640 : 480,
        boxShadow: '0 24px 60px -12px rgba(0,0,0,.25)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${C.hair}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 1.4, textTransform: 'uppercase', color: C.brand2, fontWeight: 600, marginBottom: 4 }}>Share</div>
            <h2 style={{ margin: 0, fontFamily: C.serif, fontSize: 22, fontWeight: 400, color: C.ink }}>
              {ordersStore.shareDialogData?.customerName}
            </h2>
          </div>
          <button onClick={() => ordersStore.setShareDialogData(null)} style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${C.hair2}`, background: '#fff', color: C.ink3, cursor: 'pointer', display: 'grid', placeItems: 'center' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px' }}>
          {!isShared ? (
            <>
              <Row label="有效期">
                <PillGroup value={expire} onChange={setExpire} options={[['7','7 天'],['14','14 天'],['30','30 天']]}/>
              </Row>
              <Row label="提取码">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <PillGroup value={passwordType} onChange={setPasswordType} options={[['random','系统随机'],['manual','自定义']]}/>
                  {passwordType === 'manual' && (
                    <div>
                      <input
                        value={password} maxLength={4}
                        onChange={e => { setPassword(e.target.value); setPasswordError(validate(e.target.value)); }}
                        placeholder="4位数字或字母"
                        style={{
                          padding: '9px 12px', borderRadius: 9, outline: 'none',
                          border: `1.5px solid ${passwordError ? '#e88c7c' : C.hair}`,
                          fontFamily: C.sans, fontSize: 14, width: 160, background: '#fff',
                        }}
                      />
                      {passwordError && <div style={{ fontSize: 11.5, color: '#c2412e', marginTop: 4 }}>{passwordError}</div>}
                    </div>
                  )}
                </div>
              </Row>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <button onClick={save} style={{ padding: '9px 24px', borderRadius: 9, border: 'none', background: C.brand, color: '#fff', fontFamily: C.sans, fontSize: 13, fontWeight: 500, cursor: 'pointer', boxShadow: `0 1px 0 ${C.brand2}, 0 6px 16px -8px rgba(255,122,162,.5)` }}>
                  保存分享
                </button>
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', gap: 24 }}>
              {/* Left: link info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: C.ink3, fontFamily: C.mono, marginBottom: 6 }}>分享链接</div>
                  <div style={{ padding: '10px 12px', background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 9, fontSize: 12, color: C.ink2, wordBreak: 'break-all', lineHeight: 1.5 }}>
                    {url}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 11, color: C.ink3, fontFamily: C.mono, marginBottom: 6 }}>提取码</div>
                    <div style={{ padding: '9px 14px', background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 9, fontSize: 18, fontFamily: C.mono, color: C.ink, letterSpacing: 4 }}>{password}</div>
                  </div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 16 }}>
                  <div onClick={() => setAutoFill(!autoFill)} style={{ width: 36, height: 20, borderRadius: 10, background: autoFill ? C.brand : C.hair2, transition: 'background .2s', position: 'relative', flexShrink: 0 }}>
                    <div style={{ position: 'absolute', top: 2, left: autoFill ? 18 : 2, width: 16, height: 16, borderRadius: 8, background: '#fff', transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }}/>
                  </div>
                  <span style={{ fontSize: 12.5, color: C.ink3 }}>自动填充提取码</span>
                </label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button onClick={copy} style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${C.hair2}`, background: '#fff', color: C.ink, fontFamily: C.sans, fontSize: 12, cursor: 'pointer' }}>
                    复制链接
                  </button>
                  <button data-testid="share-dialog-cancel" onClick={cancel} disabled={hasSelections} style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${C.hair2}`, background: hasSelections ? C.paper2 : '#fff', color: hasSelections ? C.ink3 : C.dangerFg, fontFamily: C.sans, fontSize: 12, cursor: hasSelections ? 'not-allowed' : 'pointer' }}>
                    取消分享
                  </button>
                </div>
                {hasSelections && (
                  <div data-testid="share-dialog-cancel-locked" style={{ marginTop: 8, fontSize: 12, color: C.ink3, lineHeight: 1.5 }}>
                    已有客户提交选片，无法取消分享。
                  </div>
                )}
              </div>

              {/* Right: QR code */}
              {qrCodeUrl && (
                <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                  <div style={{ fontSize: 11, color: C.ink3, fontFamily: C.mono }}>小程序二维码</div>
                  <img src={`data:image/png;base64,${qrCodeUrl}`} alt="小程序二维码" style={{ width: 160, height: 160, borderRadius: 8, border: `1px solid ${C.hair}` }}/>
                  <button onClick={() => {
                    if (copyImageToClipboard(qrCodeUrl)) snackbarStore.showSnackbar('图片已复制');
                    else snackbarStore.showSnackbar('复制失败', 'error');
                  }} style={{ padding: '6px 14px', borderRadius: 7, border: `1px solid ${C.hair2}`, background: '#fff', color: C.ink3, fontFamily: C.sans, fontSize: 11, cursor: 'pointer' }}>
                    复制二维码
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}));

export default ShareDialog;
